import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAutoTransferRules } from '../../hooks/useAutoTransferRules';
import { SortableRuleCard } from '../components/SortableRuleCard';
import { RuleFormModal } from '../components/RuleFormModal';
import { UserSingleSelect } from '../components/UserSingleSelect';
import { Switch, Button, Card, CardContent, Toast, Input, Tabs, TabsList, TabsTrigger, TabsContent, Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Checkbox } from '@/components/ui';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { Loader2, Plus, GripVertical } from 'lucide-react';
import { Label } from '@/components/ui';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import type { AutoTransferHistoryEntry, AutoTransferRule } from '../../types/auto-transfer.types';
import type { ToastType } from '@/components/ui/Toast';
import type { ReturnWindowUnit } from '../../hooks/useAutoTransferRules';

export function AutoTransferRulesPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get('view');

  const {
    config,
    loading,
    error,
    toggleActive,
    createRule,
    updateRule,
    deleteRule,
    reorderRules,
    setDefaultAssignedUser,
    setReturnWindow,
  } = useAutoTransferRules();

  const [saving, setSaving] = useState(false);
  const [alwaysReturn, setAlwaysReturn] = useState<boolean>(false);
  const [returnWindowValue, setReturnWindowValue] = useState<string>('72');
  const [returnWindowUnit, setReturnWindowUnit] = useState<ReturnWindowUnit>('HOURS');
  const [activeTab, setActiveTab] = useState<'rules' | 'history'>('rules');
  const [historyEntries, setHistoryEntries] = useState<AutoTransferHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; ruleId: string | null; ruleName: string }>({
    isOpen: false,
    ruleId: null,
    ruleName: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !config) return;

    const oldIndex = config.rules.findIndex(r => r.id === active.id);
    const newIndex = config.rules.findIndex(r => r.id === over.id);
    const newOrder = arrayMove(config.rules, oldIndex, newIndex);

    try {
      await reorderRules(newOrder.map(r => r.id));
      setToast({
        message: t('leads.autoTransfer.reorderSuccess') || 'Ordem das regras atualizada!',
        type: 'success',
      });
    } catch (error: any) {
      setToast({
        message: error?.response?.data?.message || error?.message || t('leads.autoTransfer.reorderError') || 'Erro ao reordenar regras',
        type: 'error',
      });
    }
  };

  const handleToggleActive = async (checked: boolean) => {
    setSaving(true);
    try {
      await toggleActive(checked);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRule = async (data: {
    name: string;
    conditions: Record<string, unknown>;
    assignedUserIds: string[];
  }) => {
    if (!data.name || !data.name.trim()) {
      setToast({
        message: t('leads.autoTransfer.ruleNameRequired'),
        type: 'error',
      });
      return;
    }

    if (!data.assignedUserIds || data.assignedUserIds.length === 0) {
      setToast({
        message: t('leads.autoTransfer.selectUsersRequired'),
        type: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      const editingId = searchParams.get('edit');
      if (editingId) {
        await updateRule(editingId, data);
        setToast({
          message: t('leads.autoTransfer.ruleUpdatedSuccess'),
          type: 'success',
        });
      } else {
        await createRule(data);
        setToast({
          message: t('leads.autoTransfer.ruleCreatedSuccess'),
          type: 'success',
        });
      }
      setSearchParams({});
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      setToast({
        message: err?.response?.data?.message || err?.message || t('leads.autoTransfer.ruleSaveError'),
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditRule = (rule: AutoTransferRule) => {
    setSearchParams({ edit: rule.id });
  };

  const handleDeleteRule = (rule: AutoTransferRule) => {
    setDeleteConfirm({
      isOpen: true,
      ruleId: rule.id,
      ruleName: rule.name,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.ruleId) return;

    setSaving(true);
    try {
      await deleteRule(deleteConfirm.ruleId);
      setToast({
        message: t('leads.autoTransfer.ruleDeletedSuccess'),
        type: 'success',
      });
    } catch (error: any) {
      setToast({
        message: error?.response?.data?.message || error?.message || t('leads.autoTransfer.deleteError'),
        type: 'error',
      });
    } finally {
      setSaving(false);
      setDeleteConfirm({ isOpen: false, ruleId: null, ruleName: '' });
    }
  };

  const handleNewRule = () => {
    setSearchParams({ view: 'form' });
  };

  const handleCloseForm = () => {
    setSearchParams({});
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await apiService.get<AutoTransferHistoryEntry[]>('/leads/auto-transfer/history');
      setHistoryEntries(Array.isArray(data) ? data : []);
    } catch (historyError: unknown) {
      const err = historyError as { response?: { data?: { message?: string } }; message?: string };
      setToast({
        message: err?.response?.data?.message || err?.message || t('leads.autoTransfer.historyLoadError'),
        type: 'error',
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveReturnRule = async () => {
    if (!alwaysReturn) {
      const parsedValue = Number(returnWindowValue);
      if (!Number.isInteger(parsedValue) || parsedValue < 1) {
        setToast({ message: t('leads.autoTransfer.returnRuleInvalid'), type: 'error' });
        return;
      }
    }

    setSaving(true);
    try {
      await setReturnWindow({
        alwaysReturn,
        returnWindowValue: alwaysReturn ? undefined : Number(returnWindowValue),
        returnWindowUnit: alwaysReturn ? undefined : returnWindowUnit,
      });
      setToast({ message: t('leads.autoTransfer.returnRuleSaved'), type: 'success' });
    } catch (changeErr: unknown) {
      const r = changeErr as { response?: { data?: { message?: string } }; message?: string };
      setToast({
        message: r?.response?.data?.message || r?.message || t('leads.autoTransfer.returnRuleError'),
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!config) return;
    setAlwaysReturn(config.alwaysReturn ?? false);
    setReturnWindowValue(String(config.returnWindowValue ?? 72));
    setReturnWindowUnit(config.returnWindowUnit ?? 'HOURS');
  }, [config?.alwaysReturn, config?.returnWindowValue, config?.returnWindowUnit]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    void loadHistory();
  }, [activeTab]);

  if (loading && !config) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        {error}
      </div>
    );
  }

  if (viewMode === 'form' || searchParams.get('edit')) {
    const editingId = searchParams.get('edit');
    const editingRule = editingId ? config?.rules.find(r => r.id === editingId) || null : null;

    return (
      <>
        <RuleFormModal
          open={true}
          onClose={handleCloseForm}
          onSave={handleSaveRule}
          rule={editingRule}
          loading={saving}
        />
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </>
    );
  }

  const actionLabelMap: Record<string, string> = {
    'leads.autoTransfer.toggle.updated': t('leads.autoTransfer.historyActionToggle'),
    'leads.autoTransfer.defaultSeller.updated': t('leads.autoTransfer.historyActionDefaultSeller'),
    'leads.autoTransfer.returnRule.updated': t('leads.autoTransfer.historyActionReturnRule'),
    'leads.autoTransfer.rule.created': t('leads.autoTransfer.historyActionRuleCreated'),
    'leads.autoTransfer.rule.updated': t('leads.autoTransfer.historyActionRuleUpdated'),
    'leads.autoTransfer.rule.deleted': t('leads.autoTransfer.historyActionRuleDeleted'),
    'leads.autoTransfer.rules.reordered': t('leads.autoTransfer.historyActionRulesReordered'),
  };

  const renderHistoryPayload = (value: unknown): string => {
    if (!value) return '-';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('leads.autoTransfer.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('leads.autoTransfer.description')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {t('leads.autoTransfer.enabled')}
          </span>
          <Switch
            checked={config?.isActive ?? false}
            onCheckedChange={handleToggleActive}
            disabled={saving}
          />
          <Button onClick={handleNewRule} disabled={saving}>
            <Plus className="mr-2 h-4 w-4" />
            {t('leads.autoTransfer.addRule')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'rules' | 'history')}>
        <TabsList>
          <TabsTrigger value="rules">{t('leads.autoTransfer.tabRules')}</TabsTrigger>
          <TabsTrigger value="history">{t('leads.autoTransfer.tabHistory')}</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="space-y-6">
          <Card>
            <CardContent className="space-y-3 py-4">
              <div className="space-y-1">
                <Label className="text-foreground">{t('leads.autoTransfer.returnRuleTitle')}</Label>
                <p className="text-xs text-muted-foreground">{t('leads.autoTransfer.returnRuleHint')}</p>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="always-return"
                  checked={alwaysReturn}
                  onCheckedChange={(checked) => setAlwaysReturn(checked === true)}
                  disabled={saving}
                />
                <Label htmlFor="always-return" className="text-sm font-normal cursor-pointer">
                  {t('leads.autoTransfer.alwaysReturn')}
                </Label>
              </div>

              {!alwaysReturn && (
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={returnWindowValue}
                    onChange={(e) => setReturnWindowValue(e.target.value)}
                    disabled={saving}
                    className="w-32"
                  />
                  <Select
                    value={returnWindowUnit}
                    onValueChange={(value) => setReturnWindowUnit(value as ReturnWindowUnit)}
                    disabled={saving}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOURS">{t('leads.autoTransfer.unitHours')}</SelectItem>
                      <SelectItem value="DAYS">{t('leads.autoTransfer.unitDays')}</SelectItem>
                      <SelectItem value="MONTHS">{t('leads.autoTransfer.unitMonths')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={handleSaveReturnRule} disabled={saving}>
                {t('common.save')}
              </Button>
            </CardContent>
          </Card>

          {(!config?.rules || config.rules.length === 0) && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <GripVertical className="mb-4 h-12 w-12 text-muted-foreground/50" />
                <p className="text-muted-foreground">
                  {t('leads.autoTransfer.noRules')}
                </p>
                <Button variant="outline" className="mt-4" onClick={handleNewRule}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('leads.autoTransfer.addFirstRule')}
                </Button>
              </CardContent>
            </Card>
          )}

          {config?.rules && config.rules.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={config.rules.map(r => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {config.rules.map(rule => (
                    <SortableRuleCard
                      key={rule.id}
                      rule={rule}
                      onEdit={() => handleEditRule(rule)}
                      onDelete={() => handleDeleteRule(rule)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <Card>
            <CardContent className="space-y-3 py-4">
              <div className="space-y-1">
                <Label className="text-foreground">{t('leads.autoTransfer.defaultSeller')}</Label>
                <p className="text-xs text-muted-foreground">{t('leads.autoTransfer.defaultSellerHint')}</p>
              </div>
              <UserSingleSelect
                value={config?.defaultAssignedUser?.id ?? null}
                disabled={saving || !config}
                onChange={async (userId) => {
                  setSaving(true);
                  try {
                    await setDefaultAssignedUser(userId);
                    setToast({ message: t('leads.autoTransfer.defaultSellerSaved'), type: 'success' });
                  } catch (changeErr: unknown) {
                    const r = changeErr as { response?: { data?: { message?: string } }; message?: string };
                    setToast({
                      message: r?.response?.data?.message || r?.message || t('leads.autoTransfer.defaultSellerError'),
                      type: 'error',
                    });
                  } finally {
                    setSaving(false);
                  }
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardContent className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label className="text-foreground">{t('leads.autoTransfer.historyTitle')}</Label>
                <Button variant="outline" onClick={() => void loadHistory()} disabled={historyLoading}>
                  {t('common.refresh')}
                </Button>
              </div>
              {historyLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : historyEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('leads.autoTransfer.historyEmpty')}</p>
              ) : (
                <div className="space-y-3">
                  {historyEntries.map((entry) => (
                    <div key={entry.id} className="rounded-lg border border-border p-3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="gray">{actionLabelMap[entry.action] ?? entry.action}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(entry.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">{entry.user?.name ?? '-'}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">{t('leads.autoTransfer.historyOldValues')}: </span>
                          <span className="text-foreground break-all">{renderHistoryPayload(entry.oldValues)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('leads.autoTransfer.historyNewValues')}: </span>
                          <span className="text-foreground break-all">{renderHistoryPayload(entry.newValues)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, ruleId: null, ruleName: '' })}
        onConfirm={confirmDelete}
        title={t('leads.autoTransfer.deleteRuleTitle')}
        description={
          deleteConfirm.ruleName
            ? t('leads.autoTransfer.deleteRuleDescription', { name: deleteConfirm.ruleName })
            : t('leads.autoTransfer.deleteRuleDescriptionGeneric')
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={saving}
      />
    </div>
  );
}